import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { IntrinsicSizeImg } from 'components/Img';
import { LayoutContainer } from 'components/Layout';
import Link from 'components/Link';
import Message from 'components/Message';
import Text from 'components/Text';
import { useIsEditing } from 'hooks';
import type { Asset } from 'models/asset';
import type { Field, SitecoreBlockProps } from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { is } from 'utils/helpers';
import { parseJson } from 'utils/string';

interface Brand {
	displayName: string;
	fields?: {
		/** JSON string in the shape of `Asset[]`. */
		brandAssetsJson?: Field<string>;
		hasProductsWithShowListing?: Field<boolean>;
		id?: Field<string>;
		title?: Field<string>;
	};
	id: string;
	name: string;
	url: string;
}

interface Props extends SitecoreBlockProps {
	fields?: {
		brands: Brand[];
		heading: Field<string>;
	};
}

const MIN_COUNT = 6;

export function getShortcutBrandLogoImage(
	brandImages: Asset[] | undefined | null,
): { alt: string; src: string } | undefined {
	if (!brandImages?.length) {
		return undefined;
	}

	const logotype = brandImages.find((asset) => asset.type === 'Logotype');
	const primaryLogoVersion = logotype?.versions?.find(
		(version) => version.subType === 'PrimaryLogo',
	);
	const mainImageFormat =
		primaryLogoVersion?.formats?.find(
			(format) => format.type === 'ODL 300px height transparent',
		) ??
		primaryLogoVersion?.formats?.find(
			(format) => format.type === 'ODL 80px height transparent',
		);

	const src = mainImageFormat?.url?.location;

	if (!src) {
		return undefined;
	}

	return {
		src,
		alt: primaryLogoVersion?.description ?? '',
	};
}

export default function ShortcutBrands({ fields, params }: Props) {
	const isEditing = useIsEditing();

	if (!fields) {
		return null;
	}

	const { heading } = fields;
	const brands = filterTruthy(
		fields.brands
			.filter((brand) => brand.fields?.hasProductsWithShowListing?.value)
			.map((brand) => ({
				url: brand.url,
				imageSrc: getShortcutBrandLogoImage(
					parseJson<Asset[]>(brand.fields?.brandAssetsJson?.value),
				)?.src,
				imageAlt: brand.fields?.title?.value,
			})),
		'imageSrc',
		'imageAlt',
	);

	if (!is.arrayWithLength(brands)) {
		return (
			<ComponentPlaceholder
				componentName="ShortcutBrands"
				description="Requires brands with images"
				className={getEditorMargin(params)}
			/>
		);
	}

	const hasEnoughBrands =
		is.arrayWithLength(brands) && brands.length >= MIN_COUNT;

	if (!isEditing && !hasEnoughBrands) {
		return null;
	}

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params)}
			withGrid
		>
			{isEditing && !hasEnoughBrands && (
				<div className="col-span-full">
					<Message
						variant="information"
						text={`Only ${brands.length} valid brands (with image + title) added, minimum amount is ${MIN_COUNT}`}
					/>
				</div>
			)}
			<Text
				as={params?.heading ?? 'h2'}
				field={heading}
				styleAs="h1"
				className="col-span-full mb-r-md"
			/>
			<div className="col-span-full grid grid-cols-4 justify-items-center gap-x-6 gap-y-8 md:flex md:flex-wrap md:gap-14 xl:gap-20">
				{/* The minimum amount is also the maximum actually displayed. */}
				{brands.slice(0, MIN_COUNT).map(({ imageAlt, imageSrc, url }) => (
					<Link
						key={url}
						href={url}
						className="col-span-2 flex items-center hover:opacity-70 md:mr-6"
					>
						<IntrinsicSizeImg
							src={imageSrc}
							alt={imageAlt}
							className="h-auto max-h-12 min-[400px]:max-w-40 md:max-h-16 xl:max-w-50"
						/>
					</Link>
				))}
			</div>
		</LayoutContainer>
	);
}
ShortcutBrands.displayName = 'ShortcutBrands';
